import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

interface ModeCard {
  icon: string;
  title: string;
  desc: string;
  color: string;
  tiltY: number;
}

const MODES: ModeCard[] = [
  { icon: "💬", title: "Chat", desc: "Standard RAG Q&A", color: COLORS.neonBlue, tiltY: -5 },
  { icon: "✉️", title: "Draft Email", desc: "Professional email from KB", color: COLORS.neonGreen, tiltY: 3 },
  { icon: "🔍", title: "Prospect Research", desc: "Intelligence brief", color: COLORS.neonPurple, tiltY: -3 },
  { icon: "⚔️", title: "Battle Card", desc: "Competitive comparison", color: COLORS.neonOrange, tiltY: 5 },
  { icon: "📋", title: "Meeting Prep", desc: "Pre-meeting briefing", color: COLORS.neonPink, tiltY: -4 },
  { icon: "📧", title: "Email Sequence", desc: "4-step outreach", color: COLORS.neonYellow, tiltY: 2 },
];

const ANTI_HALLUCINATION_RULES = [
  "Honest 'I don't know'",
  "Correct citations",
  "Section omission over padding",
];

const CARD_W = 460;
const CARD_H = 230;

export const Scene06Phase1: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Background green radial glow pulse
  const glowPulse = 0.08 + Math.sin(frame * 0.04) * 0.03;

  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        opacity: exitOpacity,
        overflow: "hidden",
      }}
    >
      {/* Background radial glow */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "40%",
          width: 1000,
          height: 600,
          transform: "translate(-50%, -50%)",
          background: `radial-gradient(ellipse, ${COLORS.neonGreen}${Math.round(glowPulse * 255).toString(16).padStart(2, "0")} 0%, transparent 65%)`,
          pointerEvents: "none",
        }}
      />

      {/* Grid */}
      <svg
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0.04 }}
      >
        <defs>
          <pattern id="grid06" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke={COLORS.neonGreen} strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid06)" />
      </svg>

      {/* Parallax depth layers */}
      <div style={{
        position: "absolute",
        inset: 0,
        transform: `translateY(${Math.sin(frame * 0.008) * 8}px) translateX(${Math.cos(frame * 0.006) * 5}px)`,
        pointerEvents: "none",
      }}>
        {[
          { x: 150, y: 200, size: 400, color: COLORS.neonGreen, speed: 0.005, phase: 0 },
          { x: 1600, y: 300, size: 350, color: COLORS.neonBlue, speed: 0.004, phase: 2 },
          { x: 900, y: 800, size: 500, color: COLORS.neonCyan, speed: 0.003, phase: 4 },
        ].map((orb, i) => (
          <div key={`parallax-${i}`} style={{
            position: "absolute",
            left: orb.x + Math.sin(frame * orb.speed + orb.phase) * 30 - orb.size / 2,
            top: orb.y + Math.cos(frame * orb.speed * 0.7 + orb.phase) * 20 - orb.size / 2,
            width: orb.size,
            height: orb.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${orb.color}06 0%, transparent 70%)`,
          }} />
        ))}
      </div>

      {/* Mid-layer floating particles */}
      {[...Array(6)].map((_, i) => {
        const px = 200 + i * 300;
        const py = 150 + (i % 3) * 300;
        const pColor = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonOrange, COLORS.neonPurple, COLORS.neonCyan][i];
        return (
          <div key={`particle-mid-${i}`} style={{
            position: "absolute",
            left: px + Math.sin(frame * 0.02 + i * 1.5) * 15,
            top: py + Math.cos(frame * 0.015 + i * 2) * 12,
            width: 4 + (i % 3) * 2,
            height: 4 + (i % 3) * 2,
            borderRadius: "50%",
            background: pColor,
            boxShadow: `0 0 8px ${pColor}80, 0 0 16px ${pColor}40`,
            opacity: 0.5 + Math.sin(frame * 0.03 + i) * 0.2,
            pointerEvents: "none",
          }} />
        );
      })}

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 28,
          left: "50%",
          transform: `translateX(-50%) translateY(${interpolate(titleAppear, [0, 1], [-20, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          })}px)`,
          opacity: interpolate(titleAppear, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
          display: "flex",
          alignItems: "center",
          gap: 20,
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            fontSize: 44,
            fontFamily: FONTS.heading,
            fontWeight: 800,
            color: COLORS.neonGreen,
            textShadow: `0 0 30px ${COLORS.neonGreen}80, 0 0 60px ${COLORS.neonGreen}30`,
          }}
        >
          Phase 1: Generation Modes
        </span>
        <div
          style={{
            padding: "6px 18px",
            borderRadius: 20,
            background: `${COLORS.neonGreen}25`,
            border: `2px solid ${COLORS.neonGreen}`,
            fontSize: 18,
            fontFamily: FONTS.mono,
            fontWeight: 700,
            color: COLORS.neonGreen,
            boxShadow: `0 0 20px ${COLORS.neonGreen}60, 0 0 40px ${COLORS.neonGreen}20`,
          }}
        >
          ✅ COMPLETE
        </div>
      </div>

      {/* 2x3 grid of mode cards */}
      <div
        style={{
          position: "absolute",
          top: 150,
          left: "50%",
          transform: `translateX(-50%) translateZ(${Math.sin(frame * 0.01) * 3}px)`,
          display: "grid",
          gridTemplateColumns: `repeat(3, ${CARD_W}px)`,
          gridTemplateRows: `repeat(2, ${CARD_H}px)`,
          gap: 32,
        }}
      >
        {MODES.map((mode, i) => {
          const cardDelay = 30 + i * 15;
          const cardAppear = spring({
            frame: frame - cardDelay,
            fps,
            config: SPRING.bouncy,
          });
          const checkDelay = cardDelay + 30;
          const checkAppear = spring({
            frame: frame - checkDelay,
            fps,
            config: SPRING.punch,
          });

          const cardOpacity = interpolate(cardAppear, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          const cardScale = interpolate(cardAppear, [0, 1], [0.7, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          const cardTranslateY = interpolate(cardAppear, [0, 1], [40, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });

          const checkScale = interpolate(checkAppear, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });

          return (
            <div
              key={i}
              style={{
                width: CARD_W,
                height: CARD_H,
                borderRadius: 16,
                background: `linear-gradient(135deg, ${mode.color}15, ${mode.color}08)`,
                border: `1.5px solid ${mode.color}50`,
                borderLeft: `4px solid ${mode.color}`,
                display: "flex",
                alignItems: "center",
                padding: "0 20px",
                gap: 16,
                opacity: cardOpacity,
                transform: `scale(${cardScale}) translateY(${cardTranslateY}px) perspective(800px) rotateY(${mode.tiltY}deg)`,
                boxShadow: `0 0 20px ${mode.color}25, 0 0 40px ${mode.color}10`,
                position: "relative",
                backdropFilter: "blur(4px)",
              } as React.CSSProperties}
            >
              <div style={{ fontSize: 56, flexShrink: 0 }}>{mode.icon}</div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 28,
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    color: mode.color,
                    textShadow: `0 0 12px ${mode.color}60`,
                  }}
                >
                  {mode.title}
                </div>
                <div
                  style={{
                    fontSize: 17,
                    fontFamily: FONTS.body,
                    color: COLORS.textMuted,
                    marginTop: 6,
                  }}
                >
                  {mode.desc}
                </div>
              </div>
              {/* Checkmark */}
              <div
                style={{
                  position: "absolute",
                  top: 10,
                  right: 14,
                  fontSize: 20,
                  transform: `scale(${checkScale})`,
                  transformOrigin: "center",
                  textShadow: `0 0 10px ${COLORS.neonGreen}`,
                }}
              >
                ✅
              </div>
            </div>
          );
        })}
      </div>

      {/* Anti-Hallucination Engine */}
      {(() => {
        const boxAppear = spring({
          frame: frame - 140,
          fps,
          config: SPRING.snappy,
        });
        return (
          <div
            style={{
              position: "absolute",
              bottom: 60,
              left: "50%",
              transform: `translateX(-50%) scale(${interpolate(boxAppear, [0, 1], [0.85, 1], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              })})`,
              opacity: interpolate(boxAppear, [0, 1], [0, 1], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              }),
              width: 1200,
              borderRadius: 18,
              background: `linear-gradient(135deg, ${COLORS.neonRed}12, ${COLORS.neonRed}06)`,
              border: `2px solid ${COLORS.neonRed}`,
              padding: "28px 40px",
              boxShadow: `0 0 30px ${COLORS.neonRed}40, 0 0 60px ${COLORS.neonRed}15`,
            } as React.CSSProperties}
          >
            <div
              style={{
                fontSize: 24,
                fontFamily: FONTS.heading,
                fontWeight: 800,
                color: COLORS.neonRed,
                textShadow: `0 0 15px ${COLORS.neonRed}80`,
                marginBottom: 20,
                textAlign: "center",
                letterSpacing: "2px",
                textTransform: "uppercase",
              }}
            >
              Anti-Hallucination Engine
            </div>
            <div style={{ display: "flex", gap: 40, justifyContent: "center" }}>
              {ANTI_HALLUCINATION_RULES.map((rule, i) => {
                const ruleAppear = spring({
                  frame: frame - 155 - i * 12,
                  fps,
                  config: SPRING.snappy,
                });
                return (
                  <div
                    key={i}
                    style={{
                      opacity: interpolate(ruleAppear, [0, 1], [0, 1], {
                        extrapolateLeft: "clamp",
                        extrapolateRight: "clamp",
                      }),
                      transform: `translateX(${interpolate(ruleAppear, [0, 1], [30, 0], {
                        extrapolateLeft: "clamp",
                        extrapolateRight: "clamp",
                      })}px)`,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "14px 24px",
                      borderRadius: 12,
                      background: `${COLORS.neonRed}10`,
                      border: `1px solid ${COLORS.neonRed}30`,
                    }}
                  >
                    <span style={{ color: COLORS.neonRed, fontSize: 20 }}>✓</span>
                    <span
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 18,
                        color: COLORS.textSecondary,
                      }}
                    >
                      {rule}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
